/**
 * LoanKnow UI Primitives
 * 
 * Replaces react-bootstrap (Button, Modal, Dropdown, Badge, Spinner, Form)
 * and MUI components with custom, design-system-aligned primitives.
 */
import { useState, useRef, useEffect, createContext, useContext } from 'react';
import { X, ChevronDown, Loader2 } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   Button
   ═══════════════════════════════════════════════════════════════ */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon: IconLeft,
  iconRight: IconRight,
  loading = false,
  disabled = false,
  className = '',
  style = {},
  ...props
}) {
  const variantClass = {
    primary: 'lk-btn-primary',
    secondary: 'lk-btn-secondary',
    ghost: 'lk-btn-ghost',
    danger: 'lk-btn-danger',
  }[variant] || 'lk-btn-primary';

  const sizeClass = {
    sm: 'lk-btn-sm',
    md: '',
    lg: 'lk-btn-lg',
    xl: 'lk-btn-xl',
    icon: 'lk-btn-icon',
  }[size] || '';

  return (
    <button
      className={`lk-btn ${variantClass} ${sizeClass} ${className}`}
      disabled={disabled || loading}
      style={style}
      {...props}
    >
      {loading ? (
        <Loader2 size={size === 'sm' ? 14 : 16} style={{ animation: 'lk-spin 0.6s linear infinite' }} />
      ) : IconLeft ? (
        <IconLeft size={size === 'sm' ? 14 : 16} />
      ) : null}
      {children}
      {IconRight && !loading && <IconRight size={size === 'sm' ? 14 : 16} />}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Badge
   ═══════════════════════════════════════════════════════════════ */
export function Badge({
  children,
  variant = 'neutral',
  dot = false,
  className = '',
  style = {},
}) {
  const variantClass = {
    neutral: 'lk-badge-neutral',
    primary: 'lk-badge-primary',
    success: 'lk-badge-success',
    warning: 'lk-badge-warning',
    danger: 'lk-badge-danger',
    info: 'lk-badge-info',
  }[variant] || 'lk-badge-neutral';

  return (
    <span
      className={`lk-badge ${variantClass} ${dot ? 'lk-badge-dot' : ''} ${className}`}
      style={style}
    >
      {children}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Surface (Card replacement)
   ═══════════════════════════════════════════════════════════════ */
export function Surface({
  children,
  variant = 'default',
  padding = 'var(--lk-space-6)',
  className = '',
  style = {},
  ...props
}) {
  const variantClass = {
    default: 'lk-surface',
    raised: 'lk-surface-raised',
    inset: 'lk-surface-inset',
    ghost: 'lk-surface-ghost',
  }[variant] || 'lk-surface';

  return (
    <div
      className={`${variantClass} ${className}`}
      style={{ padding, ...style }}
      {...props}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Modal
   ═══════════════════════════════════════════════════════════════ */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  style: outerStyle = {},
}) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && open) onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 400, md: 520, lg: 680, xl: 840 };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--lk-z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--lk-space-6)',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--lk-bg-overlay)',
          animation: 'lk-fade-in var(--lk-duration-fast) var(--lk-ease)',
        }}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: widths[size] || widths.md,
          maxHeight: '85vh',
          background: 'var(--lk-bg-elevated)',
          borderRadius: 'var(--lk-radius-xl)',
          boxShadow: 'var(--lk-shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'lk-scale-in var(--lk-duration-normal) var(--lk-ease)',
          ...outerStyle,
        }}
      >
        {/* Header */}
        {title && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--lk-space-5) var(--lk-space-6)',
            borderBottom: '1px solid var(--lk-border)',
          }}>
            <h3 style={{
              fontSize: 'var(--lk-text-lg)',
              fontWeight: 600,
              color: 'var(--lk-text-primary)',
              margin: 0,
            }}>
              {title}
            </h3>
            <button
              onClick={onClose}
              className="lk-btn lk-btn-ghost lk-btn-icon lk-btn-sm"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Body */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: 'var(--lk-space-6)',
        }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 'var(--lk-space-3)',
            padding: 'var(--lk-space-4) var(--lk-space-6)',
            borderTop: '1px solid var(--lk-border)',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Dropdown
   ═══════════════════════════════════════════════════════════════ */
export function Dropdown({ trigger, children, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          [align === 'right' ? 'right' : 'left']: 0,
          minWidth: 180,
          background: 'var(--lk-bg-elevated)',
          border: '1px solid var(--lk-border)',
          borderRadius: 'var(--lk-radius-lg)',
          boxShadow: 'var(--lk-shadow-lg)',
          padding: 'var(--lk-space-1)',
          zIndex: 'var(--lk-z-dropdown)',
          animation: 'lk-scale-in var(--lk-duration-fast) var(--lk-ease)',
        }}>
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

export function DropdownItem({ children, onClick, icon: Icon, danger = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lk-space-2)',
        width: '100%',
        padding: 'var(--lk-space-2) var(--lk-space-3)',
        border: 'none',
        borderRadius: 'var(--lk-radius-sm)',
        background: 'transparent',
        color: danger ? 'var(--lk-danger-600)' : 'var(--lk-text-secondary)',
        fontSize: 'var(--lk-text-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all var(--lk-duration-fast) var(--lk-ease)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = danger ? 'var(--lk-danger-50)' : 'var(--lk-bg-surface-2)';
        e.currentTarget.style.color = danger ? 'var(--lk-danger-700)' : 'var(--lk-text-primary)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = danger ? 'var(--lk-danger-600)' : 'var(--lk-text-secondary)';
      }}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Spinner
   ═══════════════════════════════════════════════════════════════ */
export function Spinner({ size = 'md', className = '' }) {
  const sizeClass = { sm: 'lk-spinner-sm', md: 'lk-spinner-md', lg: 'lk-spinner-lg' }[size] || 'lk-spinner-md';
  return <div className={`lk-spinner ${sizeClass} ${className}`} />;
}

/* ═══════════════════════════════════════════════════════════════
   Input
   ═══════════════════════════════════════════════════════════════ */
export function Input({
  label,
  error,
  helpText,
  size = 'md',
  className = '',
  style = {},
  ...props
}) {
  return (
    <div>
      {label && <label className="lk-label">{label}</label>}
      <input
        className={`lk-input ${error ? 'lk-input-error' : ''} ${size === 'lg' ? 'lk-input-lg' : ''} ${className}`}
        style={style}
        {...props}
      />
      {error && <div className="lk-error-text">{error}</div>}
      {helpText && !error && <div className="lk-help-text">{helpText}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Select
   ═══════════════════════════════════════════════════════════════ */
export function Select({
  label,
  error,
  helpText,
  options = [],
  placeholder,
  className = '',
  style = {},
  ...props
}) {
  return (
    <div>
      {label && <label className="lk-label">{label}</label>}
      <select
        className={`lk-input ${error ? 'lk-input-error' : ''} ${className}`}
        style={{ ...style, appearance: 'none', paddingRight: 32, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <div className="lk-error-text">{error}</div>}
      {helpText && !error && <div className="lk-help-text">{helpText}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Textarea
   ═══════════════════════════════════════════════════════════════ */
export function Textarea({
  label,
  error,
  helpText,
  className = '',
  style = {},
  ...props
}) {
  return (
    <div>
      {label && <label className="lk-label">{label}</label>}
      <textarea
        className={`lk-input ${error ? 'lk-input-error' : ''} ${className}`}
        style={{ resize: 'vertical', minHeight: 80, ...style }}
        {...props}
      />
      {error && <div className="lk-error-text">{error}</div>}
      {helpText && !error && <div className="lk-help-text">{helpText}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Checkbox
   ═══════════════════════════════════════════════════════════════ */
export function Checkbox({ label, checked, onChange, disabled, className = '', ...props }) {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--lk-space-2)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      fontSize: 'var(--lk-text-sm)',
      color: 'var(--lk-text-secondary)',
    }} className={className}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{
          width: 16,
          height: 16,
          accentColor: 'var(--lk-primary-600)',
          cursor: 'inherit',
        }}
        {...props}
      />
      {label}
    </label>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Tabs
   ═══════════════════════════════════════════════════════════════ */
export function Tabs({ tabs, activeTab, onChange, className = '' }) {
  return (
    <div style={{
      display: 'flex',
      gap: 'var(--lk-space-1)',
      borderBottom: '1px solid var(--lk-border)',
      marginBottom: 'var(--lk-space-6)',
    }} className={className}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding: 'var(--lk-space-3) var(--lk-space-4)',
            border: 'none',
            borderBottom: `2px solid ${activeTab === tab.id ? 'var(--lk-primary-600)' : 'transparent'}`,
            background: 'transparent',
            color: activeTab === tab.id ? 'var(--lk-text-primary)' : 'var(--lk-text-tertiary)',
            fontWeight: activeTab === tab.id ? 500 : 400,
            fontSize: 'var(--lk-text-sm)',
            cursor: 'pointer',
            transition: 'all var(--lk-duration-fast) var(--lk-ease)',
            marginBottom: -1,
          }}
          onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.color = 'var(--lk-text-secondary)';
          }}
          onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.color = 'var(--lk-text-tertiary)';
          }}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span style={{
              marginLeft: 6,
              fontSize: '11px',
              fontWeight: 500,
              padding: '1px 6px',
              borderRadius: 'var(--lk-radius-full)',
              background: activeTab === tab.id ? 'var(--lk-primary-50)' : 'var(--lk-gray-100)',
              color: activeTab === tab.id ? 'var(--lk-primary-700)' : 'var(--lk-gray-500)',
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Alert
   ═══════════════════════════════════════════════════════════════ */
export function Alert({ children, variant = 'info', icon: Icon, onClose, className = '', style = {} }) {
  const colors = {
    info:    { bg: 'var(--lk-info-50)', border: 'var(--lk-info-400)', color: 'var(--lk-info-600)' },
    success: { bg: 'var(--lk-success-50)', border: 'var(--lk-success-400)', color: 'var(--lk-success-700)' },
    warning: { bg: 'var(--lk-warning-50)', border: 'var(--lk-warning-400)', color: 'var(--lk-warning-700)' },
    danger:  { bg: 'var(--lk-danger-50)', border: 'var(--lk-danger-400)', color: 'var(--lk-danger-700)' },
  }[variant] || { bg: 'var(--lk-info-50)', border: 'var(--lk-info-400)', color: 'var(--lk-info-600)' };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--lk-space-3)',
        padding: 'var(--lk-space-4)',
        background: colors.bg,
        borderLeft: `3px solid ${colors.border}`,
        borderRadius: 'var(--lk-radius-md)',
        color: colors.color,
        fontSize: 'var(--lk-text-sm)',
        lineHeight: 'var(--lk-leading-normal)',
        ...style,
      }}
    >
      {Icon && <Icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />}
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            padding: 2,
            opacity: 0.6,
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Empty State
   ═══════════════════════════════════════════════════════════════ */
export function EmptyState({ icon: Icon, title, description, action, className = '' }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--lk-space-16) var(--lk-space-6)',
      textAlign: 'center',
    }} className={className}>
      {Icon && (
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--lk-radius-lg)',
          background: 'var(--lk-bg-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--lk-space-4)',
        }}>
          <Icon size={24} color="var(--lk-text-quaternary)" />
        </div>
      )}
      {title && (
        <h3 style={{
          fontSize: 'var(--lk-text-md)',
          fontWeight: 600,
          color: 'var(--lk-text-primary)',
          marginBottom: 'var(--lk-space-2)',
        }}>
          {title}
        </h3>
      )}
      {description && (
        <p style={{
          fontSize: 'var(--lk-text-sm)',
          color: 'var(--lk-text-tertiary)',
          maxWidth: 360,
          marginBottom: action ? 'var(--lk-space-5)' : 0,
        }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Stat Card
   ═══════════════════════════════════════════════════════════════ */
export function StatCard({ label, value, change, icon: Icon, trend, className = '' }) {
  const trendColor = trend === 'up' ? 'var(--lk-success-600)' : trend === 'down' ? 'var(--lk-danger-600)' : 'var(--lk-text-tertiary)';

  return (
    <Surface className={className} padding="var(--lk-space-5)">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 'var(--lk-text-xs)',
            fontWeight: 500,
            color: 'var(--lk-text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: 'var(--lk-space-2)',
          }}>
            {label}
          </div>
          <div style={{
            fontSize: 'var(--lk-text-2xl)',
            fontWeight: 700,
            color: 'var(--lk-text-primary)',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            {value}
          </div>
          {change && (
            <div style={{
              fontSize: 'var(--lk-text-xs)',
              color: trendColor,
              marginTop: 'var(--lk-space-1)',
              fontWeight: 500,
            }}>
              {change}
            </div>
          )}
        </div>
        {Icon && (
          <div style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--lk-radius-md)',
            background: 'var(--lk-bg-surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon size={18} color="var(--lk-text-tertiary)" />
          </div>
        )}
      </div>
    </Surface>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Page Header
   ═══════════════════════════════════════════════════════════════ */
export function PageHeader({ title, description, actions, breadcrumb, className = '' }) {
  return (
    <div style={{
      marginBottom: 'var(--lk-space-8)',
    }} className={className}>
      {breadcrumb && (
        <div style={{
          fontSize: 'var(--lk-text-sm)',
          color: 'var(--lk-text-tertiary)',
          marginBottom: 'var(--lk-space-2)',
        }}>
          {breadcrumb}
        </div>
      )}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--lk-space-4)',
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            fontSize: 'var(--lk-text-2xl)',
            fontWeight: 700,
            color: 'var(--lk-text-primary)',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            margin: 0,
          }}>
            {title}
          </h1>
          {description && (
            <p style={{
              fontSize: 'var(--lk-text-base)',
              color: 'var(--lk-text-tertiary)',
              margin: 0,
              marginTop: 4,
            }}>
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div style={{ display: 'flex', gap: 'var(--lk-space-3)', flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Grid
   ═══════════════════════════════════════════════════════════════ */
export function Grid({ children, cols = 1, gap = 'var(--lk-space-6)', className = '', style = {} }) {
  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: typeof cols === 'number' ? `repeat(${cols}, 1fr)` : cols,
        gap,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Stack
   ═══════════════════════════════════════════════════════════════ */
export function Stack({
  children,
  direction = 'column',
  gap = 'var(--lk-space-4)',
  align,
  justify,
  wrap,
  className = '',
  style = {},
}) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: direction,
        gap,
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? 'wrap' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
